import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faFile, faPlus, faTrash} from '@fortawesome/pro-solid-svg-icons';
import {ProjectFile} from '../../../../api/model/projectFile';
import {ContextFilePickerComponent} from '../../../util/context-file-picker/context-file-picker.component';
import {JsonSchema} from '../schema-form.types';

/** Wie eine gewählte Datei im Manifest steht — genau das, was der Python-Handler liest (id + name). */
interface SelectedFile {
  id: string;
  name: string;
}

/**
 * x-widget:files → Liste ausgewählter Projektdateien mit Entfernen-Button und einem Button, der den
 * Datei-Picker öffnet (KI-Kontext: „Dateien zur Wissenserweiterung des KI-Agenten").
 *
 * Der Wert ist eine Liste `{id, name}` und wird als Festwert am Eingang gespeichert; die
 * Workflow-Engine reicht ihn unverändert an das Modul durch.
 */
@Component({
  selector: 'sf-file-list',
  standalone: true,
  imports: [FaIconComponent, ContextFilePickerComponent],
  host: {'class': 'block w-full'},
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-2 w-full">
      @for (file of files; track file.id; let i = $index) {
        <div class="flex items-center gap-2 h-[38px] px-3 bg-gray-50 border border-gray-300 rounded-lg">
          <fa-icon [icon]="faFile" class="text-gray-400 shrink-0"></fa-icon>
          <span class="flex-1 min-w-0 truncate text-sm text-gray-900">{{ file.name }}</span>
          <button type="button" (click)="remove(i)" [disabled]="disabled" [attr.aria-label]="file.name + ' entfernen'"
                  class="shrink-0 text-gray-400 hover:text-red transition-colors
                         disabled:opacity-40 disabled:hover:text-gray-400 disabled:cursor-not-allowed">
            <fa-icon [icon]="faTrash"></fa-icon>
          </button>
        </div>
      }
      <button type="button" (click)="pickerVisible = true" [disabled]="disabled"
              class="inline-flex items-center justify-center gap-2 self-center h-[38px] px-3 py-2 rounded-lg
                     text-sm font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors
                     disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:cursor-not-allowed">
        <fa-icon [icon]="faPlus"></fa-icon>{{ addLabel }}
      </button>
    </div>

    @if (pickerVisible && !disabled) {
      <app-context-file-picker
        [excludeIds]="selectedIds"
        (selected)="onPicked($event)"
        (closed)="pickerVisible = false">
      </app-context-file-picker>
    }
  `,
})
export class SfFileListComponent {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<any>();

  pickerVisible = false;

  protected readonly faFile = faFile;
  protected readonly faTrash = faTrash;
  protected readonly faPlus = faPlus;

  get files(): SelectedFile[] {
    return Array.isArray(this.value)
      ? this.value.filter(f => f && typeof f === 'object').map(f => ({id: f.id ?? '', name: f.name ?? ''}))
      : [];
  }

  get selectedIds(): string[] {
    return this.files.map(f => f.id).filter(Boolean);
  }

  get addLabel(): string {
    return this.schema?.['x-add-label'] ?? 'Datei hinzufügen';
  }

  onPicked(picked: ProjectFile[]): void {
    const added: SelectedFile[] = picked
      .filter(file => !!file.id)
      .map(file => ({id: file.id!, name: file.name ?? ''}));
    this.pickerVisible = false;
    if (added.length) this.valueChange.emit([...this.files, ...added]);
  }

  remove(index: number): void {
    const next = this.files.filter((_, i) => i !== index);
    // Leere Liste als `undefined`: der Handler unterscheidet „kein Wert" von „leerer Wert".
    this.valueChange.emit(next.length ? next : undefined);
  }
}
